import React, { useMemo, useState } from 'react';

const formatDateTime = (dateStr) =>
  new Date(dateStr).toLocaleString('pl-PL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const STEP_LABELS = { 1: 'Krok 1', 2: 'Krok 2', 3: 'Krok 3' };

const CommunicationLog = ({ items, onTaskClick }) => {
  const taskGroups = useMemo(
    () => (items || [])
      .filter(item => Array.isArray(item.comments) && item.comments.length > 0)
      .map(item => {
        const comments = [...item.comments].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        const latest = comments[comments.length - 1];
        return {
          taskId: item._id,
          taskLabel: item.label,
          taskStep: item.step,
          comments,
          latestAt: latest?.createdAt || null,
        };
      })
      .sort((a, b) => new Date(b.latestAt) - new Date(a.latestAt)),
    [items]
  );
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  if (taskGroups.length === 0) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
        <i className="fas fa-comments" style={{ fontSize: '32px', marginBottom: '12px', display: 'block', color: '#cbd5e1' }} />
        Brak komentarzy.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {taskGroups.map(group => {
        const isExpanded = expandedTaskId === group.taskId;
        return (
          <div
            key={group.taskId}
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <button
              type="button"
              onClick={() => setExpandedTaskId(prev => (prev === group.taskId ? null : group.taskId))}
              style={{
                width: '100%',
                border: 'none',
                background: isExpanded ? '#f8fafc' : '#fff',
                cursor: 'pointer',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                textAlign: 'left',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', borderRadius: '4px', padding: '2px 7px' }}>
                    {STEP_LABELS[group.taskStep] || `Krok ${group.taskStep}`}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    {group.taskLabel}
                  </span>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    ({group.comments.length})
                  </span>
                </div>
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#94a3b8' }}>
                  Ostatnia wiadomość: {group.latestAt ? formatDateTime(group.latestAt) : '-'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {onTaskClick && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTaskClick(group.taskId);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        onTaskClick(group.taskId);
                      }
                    }}
                    style={{ fontSize: '11px', color: '#2563eb', textDecoration: 'underline' }}
                  >
                    Przejdź do zadania
                  </span>
                )}
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#64748b', fontSize: '12px' }} />
              </div>
            </button>

            {isExpanded && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 14px' }}>
                {group.comments.map(comment => {
                  const isAdmin = comment.authorRole === 'admin' || comment.authorRole === 'assistant';
                  return (
                    <div
                      key={comment._id}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                        marginBottom: '10px',
                        padding: '10px 12px',
                        background: isAdmin ? '#eff6ff' : '#f0fdf4',
                        borderRadius: '8px',
                        borderLeft: `3px solid ${isAdmin ? '#93c5fd' : '#86efac'}`,
                      }}
                    >
                      <span
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: isAdmin ? '#3b82f6' : '#22c55e',
                          marginTop: '5px',
                          flexShrink: 0,
                        }}
                      />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '4px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: '700',
                              color: isAdmin ? '#1e40af' : '#15803d',
                            }}
                          >
                            {comment.author}
                          </span>
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                            {formatDateTime(comment.createdAt)}
                          </span>
                        </div>
                        {/* eslint-disable-next-line react/no-danger */}
                        <div
                          className="msg-bubble-content"
                          style={{ fontSize: '13px', color: '#1e293b', wordBreak: 'break-word' }}
                          dangerouslySetInnerHTML={{ __html: comment.text }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CommunicationLog;
